import cn from "classnames";
import { documentBusinessTypesEnum, organizationDocumentsPolicysEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { isDesktop, ymEvent } from "helpers/system";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomScroll } from "react-custom-scroll";
import {
    createAcceptanceCertificate,
    createAcceptanceCertificateInvoice,
    createDocumentFromTemplate,
    createInvoice,
    getClassificationTypePreview,
} from "sources/books/bids";
import {
    createAcceptanceCertificate as dbCreateAcceptanceCertificate,
    createAcceptanceCertificateInvoice as dbCreateAcceptanceCertificateInvoice,
    createDocumentFromTemplate as dbCreateDocumentFromTemplate,
    createHiredCounterpartyDocumentFromTemplate as dbCreateHiredCounterpartyDocumentFromTemplate,
    createInvoice as dbCreateInvoice,
    getClassificationTypePreview as dbGetClassificationTypePreview,
} from "sources/books/distributionBids";
import { universalTransferDocumentGroupType } from "sources/books/finance";
import { createUniversalTransferDocument } from "sources/books/universalTransferDocuments";
import { getInfoToCreateDocument } from "sources/profile/organization";
import OrganizationRequiredInfoModal from "../organizationRequiredInfo";
import CreateByClassification from "./createByClassification";
import CreateDocument from "./createDocument";
import CreateUniversalTransferDocument from "./createUniversalTransferDocument";
import DocumentsList from "./documentsList";

const VIEWPORTTABLET = 801;

const documentDateField = {
    label: "Дата документа",
    inputProps: { name: "documentDate", type: "date" },
    type: "date",
};

const BID_DOCUMENTS = (bidType, { documentsPolicy } = {}) => {
    const usesUniversalTransferDocuments =
        documentsPolicy === organizationDocumentsPolicysEnum.WithUniversalTransferDocuments || documentsPolicy === 1;
    const isDistributionBid = bidType === "distributionBid";
    return {
        [documentBusinessTypesEnum.Contract]: {
            key: documentBusinessTypesEnum.Contract,
            label: "Договор-заявки",
            Component: CreateDocument,
            action: bidType === "bid" ? createDocumentFromTemplate : dbCreateDocumentFromTemplate,
            allow: bidType === "bid" || isDistributionBid,
            documentDate: documentDateField,
            targets: isDistributionBid
                ? [
                      {
                          key: "counterparty",
                          label: "С заказчиком",
                          buttonText: "Создать договор с заказчиком",
                          action: dbCreateDocumentFromTemplate,
                          getCounterparty: (bid) => bid.counterparty,
                          missingCounterpartyMessage:
                              "Для создания договора-заявки с заказчиком необходимо выбрать контрагента заказа",
                      },
                      {
                          key: "hiredCounterparty",
                          label: "С перевозчиком",
                          buttonText: "Создать договор с перевозчиком",
                          action: dbCreateHiredCounterpartyDocumentFromTemplate,
                          getCounterparty: (bid) => bid.hiredCarCounterparty,
                          missingCounterpartyMessage:
                              "Для создания договора-заявки с перевозчиком необходимо выбрать перевозчика",
                      },
                  ]
                : undefined,
        },
        [documentBusinessTypesEnum.Invoice]: {
            key: documentBusinessTypesEnum.Invoice,
            label: "Счета",
            Component: CreateByClassification,
            action: bidType === "bid" ? createInvoice : dbCreateInvoice,
            previewAction: bidType === "bid" ? getClassificationTypePreview : dbGetClassificationTypePreview,
            allow: true,
            canCreateWithoutSigningFiles: true,
            documentDate: documentDateField,
            manualValues: [
                {
                    label: "Номер документа-основания",
                    inputProps: { name: "invoiceDocumentBasis" },
                    type: "text",
                },
                {
                    label: "Дата документа-основания",
                    inputProps: { name: "invoiceDocumentBasisDate", type: "date" },
                    type: "date",
                },
            ],
        },
        [universalTransferDocumentGroupType]: {
            key: universalTransferDocumentGroupType,
            label: "УПД",
            Component: CreateUniversalTransferDocument,
            action: createUniversalTransferDocument,
            allow: bidType === "bid" && usesUniversalTransferDocuments,
            canCreateWithoutSigningFiles: true,
            documentDate: documentDateField,
        },
        [documentBusinessTypesEnum.AcceptanceCertificate]: {
            key: documentBusinessTypesEnum.AcceptanceCertificate,
            label: "Акты",
            Component: CreateByClassification,
            action: bidType === "bid" ? createAcceptanceCertificate : dbCreateAcceptanceCertificate,
            previewAction: bidType === "bid" ? getClassificationTypePreview : dbGetClassificationTypePreview,
            allow: !usesUniversalTransferDocuments,
            canCreateWithoutSigningFiles: true,
            documentDate: documentDateField,
            manualValues: [
                {
                    label: "Номер документа-основания",
                    inputProps: { name: "documentBasis" },
                    type: "text",
                },
                {
                    label: "Дата документа-основания",
                    inputProps: { name: "documentBasisDate", type: "date" },
                    type: "date",
                },
            ],
        },
        [documentBusinessTypesEnum.TaxInvoice]: {
            key: documentBusinessTypesEnum.TaxInvoice,
            label: "Счет-фактуры",
            Component: CreateByClassification,
            action: bidType === "bid" ? createAcceptanceCertificateInvoice : dbCreateAcceptanceCertificateInvoice,
            previewAction: bidType === "bid" ? getClassificationTypePreview : dbGetClassificationTypePreview,
            allow: !usesUniversalTransferDocuments,
            canCreateWithoutSigningFiles: true,
            documentDate: documentDateField,
        },
    };
};

export default function BidDocuments(props) {
    const documentsPolicy = props.organization?.documentsPolicy;

    const bidDocuments = useMemo(
        () => BID_DOCUMENTS(props.bidType, { documentsPolicy }),
        [props.bidType, documentsPolicy],
    );
    const shouldBeResponsive = useCallback(() => window.innerWidth <= VIEWPORTTABLET, []);
    const handleWindowRezise = () => setShowPanel(shouldBeResponsive());
    const [showPanel, setShowPanel] = useState(shouldBeResponsive());
    const [tab, setTab] = useState(documentBusinessTypesEnum.Contract);
    const [documentsUpdatedAt, setDocumentsUpdatedAt] = useState();
    const panels = useMemo(() => {
        return Object.values(bidDocuments).filter((item) => item.allow);
    }, [bidDocuments]);

    useEffect(() => {
        window.addEventListener("resize", handleWindowRezise);
        return () => window.removeEventListener("resize", handleWindowRezise);
    }, []);

    useEffect(() => {
        setDocumentsUpdatedAt(props.documentsUpdatedAt);
    }, [props.documentsUpdatedAt]);

    useEffect(() => {
        if (panels.length && !panels.some((item) => item.key === tab)) {
            setTab(panels[0].key);
        }
    }, [panels, tab]);

    const renderPanel = () => {
        return (
            <div className="px-3 py-2">
                {panels.map((item, i) => (
                    <div
                        key={item.key}
                        className={cn("reports__item", { "reports__item--selected": tab === item.key })}
                        onClick={() => setTab(item.key)}
                    >
                        {i + 1}. {item.label}
                    </div>
                ))}
            </div>
        );
    };

    const createAction = (action) => (reqData) => {
        switch (tab) {
            case documentBusinessTypesEnum.Contract:
                ymEvent("SaveContractApplication");
                break;
            case documentBusinessTypesEnum.Invoice:
                ymEvent("SaveInvoice");
                break;
            case documentBusinessTypesEnum.AcceptanceCertificate:
                ymEvent("SaveAct");
                break;
            case documentBusinessTypesEnum.TaxInvoice:
                ymEvent("SaveCheck");
                break;
        }
        return action(reqData).then(() => {
            setDocumentsUpdatedAt(Date.now());
            return true;
        });
    };

    const createActionWithRequiredInfo = (action, canCreateWithoutSigningFiles) => (reqData) => {
        const actionWithUpdate = createAction(action);

        if (reqData?.withoutSigningFiles || !canCreateWithoutSigningFiles) {
            return actionWithUpdate(reqData);
        }

        return getInfoToCreateDocument()
            .then((res) => {
                if (res == null) {
                    return actionWithUpdate(reqData);
                }

                if (res.hasUnfilledRequiredFields || res.hasUnfilledFinancialRequiredFields) {
                    return new Promise((resolve) => {
                        props.add({
                            name: "default",
                            title: "Заполните реквизиты",
                            windowMod: "sm",
                            CustomBody: OrganizationRequiredInfoModal,
                            onCloseAction: () => setTimeout(() => resolve(false), 0),
                            bodyData: {
                                data: res,
                                onSuccess: () => resolve(actionWithUpdate(reqData)),
                                roles: props.roles,
                            },
                        });
                    });
                }

                return actionWithUpdate(reqData);
            })
            .catch(() => actionWithUpdate(reqData));
    };

    const openForm = () => {
        const { id, cargoOwnerDictionaryItem } = props.bid;
        const config = bidDocuments[tab];

        if (!config) {
            return;
        }

        switch (tab) {
            case documentBusinessTypesEnum.Contract:
                ymEvent("ContractApplicationSection");
                break;
            case documentBusinessTypesEnum.Invoice:
                ymEvent("SubsectionInvoice");
                break;
            case documentBusinessTypesEnum.AcceptanceCertificate:
                ymEvent("FormAct");
                break;
            case documentBusinessTypesEnum.TaxInvoice:
                ymEvent("FormCheck");
                break;
        }

        const openOriginalForm = () => {
            const title =
                tab === documentBusinessTypesEnum.Contract ? (
                    <div className="d-flex align-items-baseline">
                        <span>Договор-заявка</span>
                        <a
                            className="font-size-14 ml-3"
                            href={paths.documentTemplates}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Шаблоны документов
                        </a>
                    </div>
                ) : (
                    bidDocuments[tab].label
                );
            const targets = config.targets?.map(({ getCounterparty, action, ...target }) => ({
                ...target,
                counterparty: getCounterparty(props.bid),
                action: createActionWithRequiredInfo(action, config.canCreateWithoutSigningFiles),
            }));
            const counterparty = cargoOwnerDictionaryItem || props.bid.counterparty;

            props.add({
                name: "default",
                title,
                windowMod: "sm",
                CustomBody: config.Component,
                bodyData: {
                    manualValues: config.manualValues,
                    documentDate: config.documentDate,
                    cargoOwnerDictionaryItem: counterparty,
                    counterparty,
                    targets,
                    id,
                    previewAction: config.previewAction,
                    action: createActionWithRequiredInfo(config.action, config.canCreateWithoutSigningFiles),
                },
            });
        };

        if (config.canCreateWithoutSigningFiles) {
            openOriginalForm();
            return;
        }

        getInfoToCreateDocument()
            .then((res) => {
                if (res == null) {
                    openOriginalForm();
                    return;
                }

                if (res.hasUnfilledRequiredFields || res.hasUnfilledFinancialRequiredFields) {
                    props.add({
                        name: "default",
                        title: "Заполните реквизиты",
                        windowMod: "sm",
                        CustomBody: OrganizationRequiredInfoModal,
                        bodyData: {
                            data: res,
                            onSuccess: openOriginalForm,
                            roles: props.roles,
                        },
                    });
                } else {
                    openOriginalForm();
                }
            })
            .catch(() => {
                openOriginalForm();
            });
    };

    const renderHeader = () => {
        const { title, close } = props;
        const cname = cn("asided-modal__header--bold", {
            "asided-modal__header--small": !isDesktop(),
        });
        return (
            <div className="modal-window__header">
                <div className="close close--sm" onClick={close} />
                <div className="modal-window__header-title--center">
                    <div className={cname}>{title}</div>
                </div>
            </div>
        );
    };

    const renderBody = () => {
        return (
            <div className="map-panels">
                <div className="map-panels__content">
                    <CustomScroll heightRelativeToParent="100%">{renderPanel()}</CustomScroll>
                </div>
            </div>
        );
    };

    const calcCname = () => {
        const { windowMod } = props;
        return cn("modal__window modal-window modal-window--lg modal-window--asided asided-modal", {
            [`modal-window--${windowMod}`]: !!windowMod,
        });
    };

    const cnameTogglePanelBtn = cn("asided-modal__showpanel-btn", { "asided-modal__showpanel-btn--close": !showPanel });
    const documentsType = bidDocuments[tab]?.documentType || tab;

    return (
        <div className={calcCname()}>
            {isDesktop() === false && <div className={cnameTogglePanelBtn} onClick={() => setShowPanel(!showPanel)} />}
            <div className="asided-modal__aside">
                {renderHeader()}
                {renderBody()}
            </div>
            <div className="asided-modal__content">
                <div className="h-100 d-flex flex-column">
                    <div className="container-fluid d-flex justify-content-between align-items-center py-3">
                        Список документов {props.bidType === "bid" ? "заявки" : "заказа"}
                        <div className="btn btn-sm btn-brand" onClick={openForm}>
                            Создать
                        </div>
                    </div>
                    {props.bidType === "bid" ? (
                        <DocumentsList.Bid
                            key={`${documentsType}_${documentsUpdatedAt}`}
                            bid={props.bid}
                            type={documentsType}
                            addModal={props.add}
                        />
                    ) : (
                        <DocumentsList.DistributionBid
                            key={`${documentsType}_${documentsUpdatedAt}`}
                            bid={props.bid}
                            type={documentsType}
                            addModal={props.add}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
